import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Funnel, CaretDown, CaretLeft, CaretRight, Fire, ArrowRight, X, SlidersHorizontal, SortAscending, Tag } from '@phosphor-icons/react';
import { MEAT_TYPE_IMAGES, getCategoryImage, buildCategoryTree, productsInCategory, productMatchesCategory } from './shopUtils';
import CategoryCarousel from './CategoryCarousel';
import { ProductCard, ProductRow } from './ProductCard';
import { ShopHero } from './HeroSections';
import { getBrandLogo } from './brandAssets';

// Small circular brand logo with graceful fallback to initial
const BrandDot = ({ name, color, logoUrl }) => {
  const [ok, setOk] = useState(true);
  const logo = logoUrl || getBrandLogo(name);
  if (logo && ok) {
    return (
      <span className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        <img src={logo} alt={name} className="w-full h-full object-contain p-0.5" onError={() => setOk(false)} loading="lazy" />
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-semibold" style={{ backgroundColor: (color || '#1B2A4A') + '15', color: color || '#1B2A4A' }}>
      {(name || '?')[0]}
    </span>
  );
};


const ShopView = ({
  products, categories, parentCats, colors, sort, setSort, selectedCategory, setSelectedCategory,
  searchInput, setSearchInput, debouncedSearch, displayLimit, setDisplayLimit, featured,
  id, goCategory, getLink, addItem, storeSlug, isPro, isInWishlist, toggleWishlist, store,
  priceRange, setPriceRange, productView, setProductView, expandedCats, setExpandedCats
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mobileSearchRef = useRef(null);
  const navigate = useNavigate();

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      if (debouncedSearch && !p.name.toLowerCase().includes(debouncedSearch.toLowerCase())) return false;
      const price = parseFloat(p.price) || 0;
      if (price < priceRange[0] || price > priceRange[1]) return false;
      if (selectedCategory) {
        const cat = categories.find(c => c.id === selectedCategory || c.name === selectedCategory || c.slug === selectedCategory);
        if (cat) {
          return productMatchesCategory(p, cat, categories);
        }
        // Fallback legacy match (string-based)
        return p.category === selectedCategory;
      }
      return true;
    }).sort((a, b) => {
      const aOOS = a.stock_status === 'outofstock' ? 1 : 0;
      const bOOS = b.stock_status === 'outofstock' ? 1 : 0;
      if (aOOS !== bOOS) return aOOS - bOOS;
      if (sort === 'price-asc') return (a.price || 0) - (b.price || 0);
      if (sort === 'price-desc') return (b.price || 0) - (a.price || 0);
      if (sort === 'name') return a.name.localeCompare(b.name);
      return 0;
    });
  }, [products, debouncedSearch, selectedCategory, sort, categories, priceRange]);

  const maxPrice = useMemo(() => Math.max(...products.map(p => parseFloat(p.price) || 0)), [products]);
  const displayProducts = filtered.slice(0, displayLimit);
  const cardProps = { colors, categories, getLink, isInWishlist, toggleWishlist, store, addItem, storeSlug, isPro };

  const activeFilterCount = (selectedCategory ? 1 : 0) + (debouncedSearch ? 1 : 0) + (priceRange[0] > 0 || priceRange[1] < maxPrice ? 1 : 0) + (sort !== 'default' ? 1 : 0);
  const activeCatName = selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : null;

  /* ═══ MOBILE FILTER DRAWER ═══ */
  const MobileDrawer = () => (
    <>
      {/* Overlay */}
      <div
        className={`md:hidden fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setDrawerOpen(false)}
        data-testid="drawer-overlay"
      />
      {/* Drawer panel */}
      <div
        className={`md:hidden fixed top-0 left-0 bottom-0 w-[85vw] max-w-[340px] bg-white z-[70] shadow-2xl transition-transform duration-300 ease-out flex flex-col ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="mobile-filter-drawer"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Funnel size={18} weight="fill" style={{ color: colors.primary }} />
            <span className="text-[16px] font-bold" style={{ color: colors.dark }}>Filtres</span>
            {activeFilterCount > 0 && (
              <span className="w-[20px] h-[20px] rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          <button onClick={() => setDrawerOpen(false)} className="w-[34px] h-[34px] rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200" data-testid="drawer-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Drawer body — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

          {/* Active filters */}
          {(activeCatName || debouncedSearch) && (
            <div className="flex flex-wrap gap-1.5" data-testid="drawer-active-filters">
              {activeCatName && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white"
                  style={{ backgroundColor: colors.primary }}>
                  {activeCatName}
                  <button onClick={() => setSelectedCategory('')}><X size={10} weight="bold" /></button>
                </span>
              )}
              {debouncedSearch && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white"
                  style={{ backgroundColor: colors.primary }}>
                  "{debouncedSearch}"
                  <button onClick={() => setSearchInput('')}><X size={10} weight="bold" /></button>
                </span>
              )}
            </div>
          )}

          {/* ── FEATURED CATEGORIES (Brasero, Kamado, Accessoires...) ── */}
          {(() => {
            const tree = buildCategoryTree(categories, products);
            const catCount = (cat) => productsInCategory(products, cat, categories).length;
            const catProductsList = (cat) => productsInCategory(products, cat, categories);

            return (
              <>
                {tree.featured.length > 0 && (
                  <div>
                    <h4 className="text-[13px] font-bold text-slate-700 mb-2.5 uppercase tracking-wider">Nos univers</h4>
                    <div className="grid grid-cols-3 gap-2.5">
                      {tree.featured.map(c => {
                        const count = catCount(c);
                        const img = getCategoryImage(c, catProductsList(c));
                        const isActive = selectedCategory === c.name || selectedCategory === c.slug;
                        return (
                          <button key={c.slug}
                            onClick={() => { setSelectedCategory(isActive ? '' : c.name); setSearchInput(''); }}
                            className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all ${isActive ? 'border-transparent shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                            style={isActive ? { backgroundColor: colors.primary + '12', borderColor: colors.primary } : {}}
                            data-testid={`drawer-featured-${c.slug}`}>
                            {img ? <img src={img} alt={c.name} className="w-[52px] h-[52px] rounded-full object-cover shadow-sm" />
                              : <span className="w-[52px] h-[52px] rounded-full bg-slate-100 flex items-center justify-center text-[16px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                            <span className={`text-[12px] font-semibold text-center leading-tight ${isActive ? '' : 'text-slate-600'}`}
                              style={isActive ? { color: colors.primary } : {}}>{c.name}</span>
                            {count > 0 && <span className="text-[10px] text-slate-400">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── SEPARATOR ── */}
                <div className="border-t border-slate-100" />

                {/* ── MEAT & FOOD CATEGORIES with subcategories ── */}
                <div>
                  <h4 className="text-[13px] font-bold text-slate-700 mb-2.5 uppercase tracking-wider">Catégories</h4>
                  <div className="space-y-0.5">
                    <button onClick={() => { setSelectedCategory(''); setSearchInput(''); }}
                      className="w-full text-left py-2.5 px-3 text-[14px] transition-all flex items-center justify-between rounded-lg hover:bg-slate-50"
                      style={{ color: !selectedCategory ? colors.primary : '#555', fontWeight: !selectedCategory ? 700 : 400, backgroundColor: !selectedCategory ? colors.primary + '10' : 'transparent' }}
                      data-testid="drawer-cat-all">
                      <span>Toutes les catégories</span>
                      <span className="text-[12px] text-slate-400 bg-white rounded-full px-2 py-0.5 shadow-sm">{products.length}</span>
                    </button>
                    {tree.meats.map(({ parent: c, children }) => {
                      const allChildrenAndSelf = [c, ...children];
                      const count = allChildrenAndSelf.reduce((acc, x) => acc + productsInCategory(products, x, categories).length, 0);
                      const isActive = selectedCategory === c.name || children.some(ch => selectedCategory === ch.name);
                      const isExpanded = expandedCats[c.slug] || isActive;
                      const img = getCategoryImage(c, catProductsList(c));
                      const isBrandParent = (c.slug === 'marques') || ((c.name || '').toLowerCase() === 'marques');
                      return (
                        <div key={c.slug}>
                          <div className="flex items-center">
                            <button onClick={() => { setSelectedCategory(isActive && !children.length ? '' : c.name); setSearchInput(''); if (children.length) setExpandedCats(prev => ({...prev, [c.slug]: !prev[c.slug]})); }}
                              className="flex-1 text-left py-2.5 px-3 text-[14px] transition-all flex items-center gap-3 rounded-lg hover:bg-slate-50"
                              style={{ color: selectedCategory === c.name ? colors.primary : '#555', fontWeight: selectedCategory === c.name ? 700 : 400, backgroundColor: selectedCategory === c.name ? colors.primary + '10' : 'transparent' }}
                              data-testid={`drawer-cat-${c.slug}`}>
                              {isBrandParent
                                ? <span className="w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primary + '14', color: colors.primary }}><Tag size={17} weight="fill" /></span>
                                : img
                                  ? <img src={img} alt="" className="w-[36px] h-[36px] rounded-full object-cover border-2 border-slate-200 flex-shrink-0" />
                                  : <span className="w-[36px] h-[36px] rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-[12px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="text-[12px] text-slate-400">{count}</span>
                            </button>
                            {children.length > 0 && (
                              <button onClick={() => setExpandedCats(prev => ({...prev, [c.slug]: !prev[c.slug]}))}
                                className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors">
                                <CaretDown size={12} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                              </button>
                            )}
                          </div>
                          {isExpanded && children.length > 0 && (
                            <div className="ml-7 border-l-2 border-slate-100 pl-2.5 space-y-0.5">
                              {children.map(ch => {
                                const chCount = catCount(ch);
                                return (
                                  <button key={ch.slug} onClick={() => { setSelectedCategory(ch.id || ch.name); setSearchInput(''); setDrawerOpen(false); }}
                                    className="w-full text-left py-1.5 px-2 text-[12px] transition-all flex items-center gap-2 rounded-md hover:bg-slate-50"
                                    style={{ color: selectedCategory === ch.id || selectedCategory === ch.name ? colors.primary : '#777', fontWeight: selectedCategory === ch.id || selectedCategory === ch.name ? 600 : 400 }}>
                                    {isBrandParent && <BrandDot name={ch.name} color={colors.primary} logoUrl={ch.brand_logo} />}
                                    <span className="truncate flex-1">{ch.name}</span>
                                    {!isBrandParent && <span className="text-[10px] text-slate-400">{chCount}</span>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Remaining categories not in hierarchy */}
                    {tree.remaining.map(c => {
                      const count = catCount(c);
                      const img = getCategoryImage(c, catProductsList(c));
                      return (
                        <button key={c.slug} onClick={() => { setSelectedCategory(selectedCategory === c.name ? '' : c.name); setSearchInput(''); }}
                          className="w-full text-left py-2 px-3 text-[13px] transition-all flex items-center gap-2.5 rounded-lg hover:bg-slate-50"
                          style={{ color: selectedCategory === c.name ? colors.primary : '#555', fontWeight: selectedCategory === c.name ? 700 : 400, backgroundColor: selectedCategory === c.name ? colors.primary + '10' : 'transparent' }}
                          data-testid={`drawer-cat-${c.slug}`}>
                          {img
                            ? <img src={img} alt="" className="w-[28px] h-[28px] rounded-full object-cover border-2 border-slate-200 flex-shrink-0" />
                            : <span className="w-[28px] h-[28px] rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                          <span className="flex-1 truncate">{c.name}</span>
                          <span className="text-[11px] text-slate-400">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Prix */}
          <div>
            <h4 className="text-[13px] font-bold text-slate-700 mb-2.5 uppercase tracking-wider">Prix</h4>
            <div className="space-y-3">
              <input type="range" min={0} max={maxPrice} step={1} value={priceRange[1]}
                onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                className="w-full h-[6px] rounded-full appearance-none cursor-pointer"
                style={{ accentColor: colors.primary, background: `linear-gradient(to right, ${colors.primary} 0%, ${colors.primary} ${(priceRange[1] / maxPrice) * 100}%, #e2e8f0 ${(priceRange[1] / maxPrice) * 100}%, #e2e8f0 100%)` }}
                data-testid="drawer-price-slider" />
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-slate-400 font-medium mb-1 block">Min (€)</label>
                  <input type="number" min={0} max={priceRange[1]} value={priceRange[0]}
                    onChange={e => setPriceRange([Math.max(0, parseInt(e.target.value) || 0), priceRange[1]])}
                    className="w-full h-[34px] px-2.5 text-[13px] font-semibold border border-slate-200 rounded-lg outline-none focus:border-slate-400 bg-white"
                    style={{ color: colors.primary }} data-testid="drawer-price-min" />
                </div>
                <span className="text-slate-300 font-bold mt-4">—</span>
                <div className="flex-1">
                  <label className="text-[10px] text-slate-400 font-medium mb-1 block">Max (€)</label>
                  <input type="number" min={priceRange[0]} max={maxPrice} value={priceRange[1]}
                    onChange={e => setPriceRange([priceRange[0], Math.min(maxPrice, parseInt(e.target.value) || maxPrice)])}
                    className="w-full h-[34px] px-2.5 text-[13px] font-semibold border border-slate-200 rounded-lg outline-none focus:border-slate-400 bg-white"
                    style={{ color: colors.primary }} data-testid="drawer-price-max" />
                </div>
              </div>
            </div>
          </div>

          {/* Tri */}
          <div>
            <h4 className="text-[13px] font-bold text-slate-700 mb-2.5 uppercase tracking-wider">Trier par</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'default', l: 'Par défaut' },
                { v: 'price-asc', l: 'Prix croissant' },
                { v: 'price-desc', l: 'Prix décroissant' },
                { v: 'name', l: 'Nom (A-Z)' },
              ].map(opt => (
                <button key={opt.v} onClick={() => setSort(opt.v)}
                  className={`text-[12px] font-semibold py-2.5 px-3 rounded-lg border transition-all text-center ${sort === opt.v ? 'text-white border-transparent shadow-sm' : 'text-slate-500 border-slate-200 bg-white hover:border-slate-300'}`}
                  style={sort === opt.v ? { backgroundColor: colors.primary } : {}}
                  data-testid={`drawer-sort-${opt.v}`}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Drawer footer */}
        <div className="px-4 py-3 border-t border-slate-100 flex-shrink-0 flex gap-2">
          {activeFilterCount > 0 && (
            <button onClick={() => { setSelectedCategory(''); setSearchInput(''); setPriceRange([0, maxPrice]); setSort('default'); }}
              className="flex-1 py-2.5 text-[13px] font-semibold rounded-xl border-2 transition-all"
              style={{ color: colors.primary, borderColor: colors.primary }}
              data-testid="drawer-reset">
              Réinitialiser
            </button>
          )}
          <button onClick={() => setDrawerOpen(false)}
            className="flex-1 py-2.5 text-[13px] font-bold text-white rounded-xl transition-all hover:brightness-110"
            style={{ backgroundColor: colors.primary }}
            data-testid="drawer-apply">
            Voir {filtered.length} produit{filtered.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-[#eef1f5] min-h-[60vh]">
      <ShopHero id={id} colors={colors} categories={categories} selectedCategory={selectedCategory} goCategory={goCategory} />

      {/* ═══ MOBILE STICKY BAR (compact: search + filter button) ═══ */}
      <div className="md:hidden sticky top-[50px] z-30 bg-white border-b border-slate-100 shadow-sm" data-testid="mobile-sticky-bar">
        <div className="flex items-center gap-2 px-3 py-2.5">
          {/* Search */}
          <div className="flex-1 flex items-center border border-slate-200 rounded-full overflow-hidden bg-slate-50/80 h-[38px]">
            <span className="w-[34px] flex items-center justify-center text-slate-400 flex-shrink-0"><MagnifyingGlass size={15} /></span>
            <input
              ref={mobileSearchRef}
              placeholder="Rechercher..."
              value={searchInput}
              onChange={e => { setSearchInput(e.target.value); if (e.target.value) setSelectedCategory(''); }}
              className="flex-1 h-full pr-2 text-[13px] outline-none bg-transparent"
              data-testid="store-search"
            />
            {searchInput && (
              <button onClick={() => { setSearchInput(''); mobileSearchRef.current?.focus(); }} className="pr-2.5 text-slate-400">
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
          {/* Filter button */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative h-[40px] px-5 rounded-full flex items-center gap-2 text-[13px] font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            style={{ backgroundColor: colors.accent || '#FF004D' }}
            data-testid="mobile-filter-btn">
            <Funnel size={16} weight="fill" />
            Filtres
            {activeFilterCount > 0 && (
              <span className="w-[18px] h-[18px] rounded-full text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: '#fff', color: colors.accent || '#FF004D' }}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
        {/* Active filter chips — quick horizontal strip */}
        {(activeCatName || debouncedSearch) && (
          <div className="flex items-center gap-1.5 px-3 pb-2 overflow-x-auto scrollbar-hide">
            {activeCatName && (
              <button onClick={() => setSelectedCategory('')}
                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white flex-shrink-0"
                style={{ backgroundColor: colors.primary }}
                data-testid="chip-category">
                {activeCatName} <X size={10} weight="bold" />
              </button>
            )}
            {debouncedSearch && !activeCatName && (
              <button onClick={() => setSearchInput('')}
                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white flex-shrink-0"
                style={{ backgroundColor: colors.primary }}
                data-testid="chip-search">
                "{debouncedSearch}" <X size={10} weight="bold" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mobile filter drawer */}
      <MobileDrawer />

      {/* ═══ DESKTOP STICKY CATEGORY BAR (auto-scroll carousel) ═══ */}
      <div className="hidden md:block sticky top-[50px] z-30 bg-white border-b border-slate-100 shadow-sm" data-testid="shop-category-bar">
        <CategoryCarousel
          parentCats={parentCats}
          categories={categories}
          selectedCategory={selectedCategory}
          goCategory={goCategory}
          setSearchInput={setSearchInput}
          setSelectedCategory={setSelectedCategory}
          colors={colors}
          debouncedSearch={debouncedSearch}
        />
      </div>

      {/* Desktop search bar */}
      <div className="hidden md:block max-w-[1300px] mx-auto px-4 md:px-6 pt-5 pb-2">
        <div className="max-w-[520px] mx-auto">
          <div className="flex items-center border border-slate-200 rounded-full overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
            <span className="w-[44px] h-[44px] flex items-center justify-center text-slate-400 flex-shrink-0"><MagnifyingGlass size={18} /></span>
            <input placeholder="Rechercher un produit..." value={searchInput}
              onChange={e => { setSearchInput(e.target.value); if (e.target.value) setSelectedCategory(''); }}
              className="flex-1 h-[44px] pr-4 text-[14px] outline-none bg-transparent" data-testid="store-search-desktop" />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="pr-4 text-slate-400 hover:text-slate-600">
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="max-w-[1300px] mx-auto px-4 md:px-6 py-4">
        <div className="flex gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden md:block w-[260px] flex-shrink-0" data-testid="shop-sidebar">
            <div className="sticky top-[130px] max-h-[calc(100vh-140px)] overflow-y-auto overflow-x-hidden pb-4 custom-scrollbar space-y-4">
              <div>
                <h3 className="text-[14px] font-bold mb-2.5 flex items-center gap-2" style={{ color: colors.dark }}>
                  <Funnel size={15} weight="fill" style={{ color: colors.primary }} /> Catégories
                </h3>
                <ul className="space-y-0.5">
                  <li>
                    <button onClick={() => setSelectedCategory('')}
                      className="w-full text-left py-2 px-3 text-[13px] transition-all flex items-center justify-between rounded-lg hover:bg-slate-50"
                      style={{ color: !selectedCategory ? colors.primary : '#555', fontWeight: !selectedCategory ? 700 : 400, backgroundColor: !selectedCategory ? colors.primary + '10' : 'transparent' }}>
                      <span>Toutes les catégories</span>
                      <span className="text-[11px] text-slate-400 bg-white rounded-full px-2 py-0.5 shadow-sm">{products.length}</span>
                    </button>
                  </li>
                  {parentCats.map(c => {
                    const children = categories.filter(ch => ch.parent_id === c.id);
                    const isBrandParent = (c.slug === 'marques') || ((c.name || '').toLowerCase() === 'marques');
                    const catProds = productsInCategory(products, c, categories);
                    const count = catProds.length;
                    const isActive = selectedCategory === c.id || children.some(ch => ch.id === selectedCategory);
                    const isExpanded = expandedCats[c.id] || isActive;
                    const img = getCategoryImage(c, catProds);
                    return (
                      <li key={c.id}>
                        <div className="flex items-center">
                          <button onClick={() => { setSelectedCategory(c.id); setSearchInput(''); if (children.length) setExpandedCats(prev => ({...prev, [c.id]: !prev[c.id]})); }}
                            className="flex-1 text-left py-2 px-3 text-[13px] transition-all flex items-center gap-2.5 rounded-lg hover:bg-slate-50"
                            style={{ color: selectedCategory === c.id ? colors.primary : '#555', fontWeight: selectedCategory === c.id ? 700 : 400, backgroundColor: selectedCategory === c.id ? colors.primary + '10' : 'transparent' }}
                            data-testid={`sidebar-cat-${c.id}`}>
                            {isBrandParent
                              ? <span className="w-[28px] h-[28px] rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primary + '14', color: colors.primary }}><Tag size={15} weight="fill" /></span>
                              : img
                                ? <img src={img} alt="" className="w-[28px] h-[28px] rounded-full object-cover border-2 border-slate-200 flex-shrink-0" />
                                : <span className="w-[28px] h-[28px] rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-[10px] font-bold" style={{ color: colors.primary }}>{c.name[0]}</span>}
                            <span className="flex-1 truncate">{c.name}</span>
                            <span className="text-[11px] text-slate-400">{count}</span>
                          </button>
                          {children.length > 0 && (
                            <button onClick={() => setExpandedCats(prev => ({...prev, [c.id]: !prev[c.id]}))}
                              className="p-1 text-slate-400 hover:text-slate-600 transition-colors">
                              <CaretDown size={12} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>
                          )}
                        </div>
                        {isExpanded && children.length > 0 && (
                          <ul className="ml-6 border-l-2 border-slate-100 pl-2.5 space-y-0.5">
                            {children.map(ch => {
                              const chCount = products.filter(p => p.category === ch.name || p.category === ch.id).length;
                              return (
                                <li key={ch.id}>
                                  <button onClick={() => { setSelectedCategory(ch.id); setSearchInput(''); }}
                                    className="w-full text-left py-1.5 px-2 text-[12px] transition-all flex items-center gap-2 rounded-md hover:bg-slate-50"
                                    style={{ color: selectedCategory === ch.id ? colors.primary : '#777', fontWeight: selectedCategory === ch.id ? 600 : 400 }}>
                                    {isBrandParent && <BrandDot name={ch.name} color={colors.primary} logoUrl={ch.brand_logo} />}
                                    <span className="truncate flex-1">{ch.name}</span>
                                    {!isBrandParent && <span className="text-[10px] text-slate-400">{chCount}</span>}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <h3 className="text-[14px] font-bold mb-3" style={{ color: colors.dark }}>Filtrer par prix</h3>
                <div className="space-y-3">
                  <input type="range" min={0} max={maxPrice} step={1} value={priceRange[1]}
                    onChange={e => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full h-[6px] rounded-full appearance-none cursor-pointer"
                    style={{ accentColor: colors.primary, background: `linear-gradient(to right, ${colors.primary} 0%, ${colors.primary} ${(priceRange[1] / maxPrice) * 100}%, #e2e8f0 ${(priceRange[1] / maxPrice) * 100}%, #e2e8f0 100%)` }}
                    data-testid="price-range-slider" />
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-400 font-medium mb-1 block">Min (€)</label>
                      <input type="number" min={0} max={priceRange[1]} value={priceRange[0]}
                        onChange={e => setPriceRange([Math.max(0, parseInt(e.target.value) || 0), priceRange[1]])}
                        className="w-full h-[34px] px-2.5 text-[13px] font-semibold border border-slate-200 rounded-lg outline-none focus:border-slate-400 bg-white"
                        style={{ color: colors.primary }} data-testid="price-min-input" />
                    </div>
                    <span className="text-slate-300 font-bold mt-4">—</span>
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-400 font-medium mb-1 block">Max (€)</label>
                      <input type="number" min={priceRange[0]} max={maxPrice} value={priceRange[1]}
                        onChange={e => setPriceRange([priceRange[0], Math.min(maxPrice, parseInt(e.target.value) || maxPrice)])}
                        className="w-full h-[34px] px-2.5 text-[13px] font-semibold border border-slate-200 rounded-lg outline-none focus:border-slate-400 bg-white"
                        style={{ color: colors.primary }} data-testid="price-max-input" />
                    </div>
                  </div>
                  {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
                    <button onClick={() => setPriceRange([0, maxPrice])}
                      className="text-[12px] font-medium hover:underline" style={{ color: colors.primary }}>Réinitialiser</button>
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <h3 className="text-[14px] font-bold mb-3" style={{ color: colors.dark }}>Trier par</h3>
                <select value={sort} onChange={e => setSort(e.target.value)}
                  className="w-full text-[13px] border border-slate-200 rounded-lg px-3 py-2.5 outline-none bg-white" data-testid="sort-select">
                  <option value="default">Par défaut</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="name">Nom (A-Z)</option>
                </select>
              </div>
              {(selectedCategory || debouncedSearch || priceRange[0] > 0 || priceRange[1] < maxPrice) && (
                <button onClick={() => { setSelectedCategory(''); setSearchInput(''); setPriceRange([0, maxPrice]); setSort('default'); }}
                  className="w-full py-2.5 text-[13px] font-semibold text-white rounded-xl transition-all hover:brightness-110"
                  style={{ backgroundColor: colors.primary }} data-testid="reset-all-filters">
                  Réinitialiser tous les filtres
                </button>
              )}
            </div>
          </aside>

          {/* Product grid */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4 gap-3">
              <p className="text-[14px] text-slate-500 font-medium flex-shrink-0">
                {filtered.length} produit{filtered.length !== 1 ? 's' : ''}
                {selectedCategory && categories.find(c => c.id === selectedCategory) ? ` dans "${categories.find(c => c.id === selectedCategory)?.name}"` : ''}
                {debouncedSearch && !selectedCategory ? ` pour "${debouncedSearch}"` : ''}
              </p>
              <div className="hidden md:flex items-center gap-2">
                <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden" data-testid="view-toggle">
                  <button onClick={() => setProductView('grid')}
                    className={`px-3 py-2 text-[12px] font-medium transition-colors ${productView === 'grid' ? 'text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                    style={productView === 'grid' ? { backgroundColor: colors.primary } : {}}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="7" height="7" rx="1.5"/><rect x="9" y="0" width="7" height="7" rx="1.5"/><rect x="0" y="9" width="7" height="7" rx="1.5"/><rect x="9" y="9" width="7" height="7" rx="1.5"/></svg>
                  </button>
                  <button onClick={() => setProductView('list')}
                    className={`px-3 py-2 text-[12px] font-medium transition-colors ${productView === 'list' ? 'text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                    style={productView === 'list' ? { backgroundColor: colors.primary } : {}}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="16" height="4" rx="1.5"/><rect x="0" y="6" width="16" height="4" rx="1.5"/><rect x="0" y="12" width="16" height="4" rx="1.5"/></svg>
                  </button>
                </div>
              </div>
            </div>
            {productView === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                {displayProducts.map(p => <ProductCard key={p.id} p={p} {...cardProps} />)}
              </div>
            ) : (
              <div className="space-y-3">
                {displayProducts.map(p => <ProductRow key={p.id} p={p} colors={colors} getLink={getLink} addItem={addItem} storeSlug={storeSlug} isPro={isPro} store={store} />)}
              </div>
            )}
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <MagnifyingGlass size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-[16px] text-slate-400">Aucun produit trouvé</p>
                <button onClick={() => { setSearchInput(''); setSelectedCategory(''); }}
                  className="mt-3 text-[14px] font-medium" style={{ color: colors.primary }}>Réinitialiser les filtres</button>
              </div>
            )}
            {filtered.length > displayLimit && (
              <div className="text-center mt-10">
                <button onClick={() => setDisplayLimit(prev => prev + 24)}
                  className="px-8 py-3 rounded-full text-white text-[14px] font-bold transition-all hover:scale-105 hover:shadow-lg"
                  style={{ backgroundColor: colors.primary }} data-testid="load-more-btn">
                  Voir plus de produits ({filtered.length - displayLimit} restants)
                </button>
              </div>
            )}
            {filtered.length > 0 && filtered.length <= displayLimit && displayLimit > 24 && (
              <p className="text-center mt-8 text-[14px] text-slate-400">Tous les {filtered.length} produits affichés</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopView;
